// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import * as THREE from 'three';

import type { BuildingElement, ElementCategory } from '../api/types';
import { ELEMENT_CATEGORIES, elementColorToken, isTranslucentCategory } from '../bygning/elementGeometry';
import type { PointCloudScene } from './PointCloudScene';

/** Fallback when a token cannot be resolved (the `--mesh-surface` placeholder). */
const FALLBACK = '#8a8f99';
/** How much of a space shows: enough to read the room, not enough to hide the walls behind it. */
const SPACE_OPACITY = 0.1;
const SPACE_SELECTED_OPACITY = 0.38;

interface CategoryMaterials {
  surface: THREE.MeshStandardMaterial;
  cap: THREE.MeshBasicMaterial | null;
}

/**
 * The typed building model as per-element solids (frontend-r22.27) — the
 * Ground's 3D model in place of the reconstruction mesh. One `THREE.Mesh`
 * per element (`GET /elements`, or `elementsFromModel`), one lit material
 * per category in its colour token (`elementColorToken`), spaces
 * see-through; the selected element is drawn in `--color-accent`.
 *
 * Like `MeshScene`, the meshes live in the host {@link PointCloudScene}:
 * same camera, lights and global clipping planes (the clipping box, which
 * Etage sets, and the plan's section), and the same recentring origin — the
 * element vertices are made origin-relative in double precision before they
 * become float32, so georeferenced coordinates keep their millimetres. That
 * subtraction is the whole world -> scene transform: the group itself sits
 * at the scene origin (unlike `MeshScene`, whose float32 world vertices are
 * moved by the group), so an element lands on the cloud points it was
 * measured from.
 * Closed solids get section caps (back faces in the surface colour, pulled
 * forward), so a cut wall or slab reads as solid poché.
 */
export class ElementsScene {
  private group: THREE.Group | null = null;
  private readonly materials = new Map<ElementCategory, CategoryMaterials>();
  private selectedSolid: THREE.MeshStandardMaterial | null = null;
  private selectedSpace: THREE.MeshStandardMaterial | null = null;
  private wireframeMaterial: THREE.MeshBasicMaterial | null = null;
  private meshes: THREE.Mesh[] = [];
  private caps: THREE.Mesh[] = [];
  private hidden: ReadonlySet<string> = new Set();
  private selected: string | null = null;
  private wireframe = false;
  private visible = true;

  constructor(private readonly host: PointCloudScene) {}

  /** Replace the drawn elements. Returns false when there was nothing to draw. */
  setElements(elements: readonly BuildingElement[]): boolean {
    this.clear();
    if (elements.length === 0) return false;

    const box = new THREE.Box3();
    for (const e of elements) {
      const p = e.positions;
      for (let i = 0; i + 2 < p.length; i += 3) box.expandByPoint(new THREE.Vector3(p[i], p[i + 1], p[i + 2]));
    }
    if (box.isEmpty()) return false;
    const origin = this.host.registerExternalBounds(box);

    this.ensureMaterials();
    const group = new THREE.Group();
    for (const e of elements) {
      const p = e.positions;
      const count = Math.floor(p.length / 3);
      if (count === 0 || e.indices.length < 3) continue;
      const local = new Float32Array(count * 3);
      for (let i = 0; i < count; i += 1) {
        local[i * 3] = p[i * 3] - origin.x;
        local[i * 3 + 1] = p[i * 3 + 1] - origin.y;
        local[i * 3 + 2] = p[i * 3 + 2] - origin.z;
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(local, 3));
      geometry.setIndex(count > 65535 ? new THREE.Uint32BufferAttribute(e.indices, 1) : new THREE.Uint16BufferAttribute(e.indices, 1));
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      const mats = this.materials.get(e.category) ?? this.materials.get('wall')!;
      const mesh = new THREE.Mesh(geometry, mats.surface);
      mesh.userData.elementGuid = e.guid;
      mesh.userData.elementCategory = e.category;
      // The measure tools snap to its corners; not to the air of a space.
      mesh.userData.snapTarget = !isTranslucentCategory(e.category);
      // Spaces after the solids: see-through surfaces sort last.
      mesh.renderOrder = isTranslucentCategory(e.category) ? 2 : 0;
      group.add(mesh);
      this.meshes.push(mesh);
      if (mats.cap) {
        const cap = new THREE.Mesh(geometry, mats.cap);
        cap.userData.elementGuid = e.guid;
        cap.userData.elementCategory = e.category;
        group.add(cap);
        this.caps.push(cap);
      }
    }
    // The vertices are already scene-local (world minus `origin`, above), so
    // the group stays at the scene origin. `MeshScene` keeps its vertices in
    // world and moves its group by -origin instead; doing both here put every
    // element a second `origin` away from the point cloud.
    this.host.sceneRoot().add(group);
    this.group = group;
    this.apply();
    return this.meshes.length > 0;
  }

  setHidden(hidden: ReadonlySet<string>): void {
    this.hidden = hidden;
    this.apply();
  }

  setSelected(guid: string | null): void {
    this.selected = guid;
    this.apply();
  }

  setWireframe(wireframe: boolean): void {
    this.wireframe = wireframe;
    this.apply();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this.group) this.group.visible = visible;
  }

  /** Re-read every colour from its token (a theme switch). */
  refreshColors(): void {
    for (const [category, mats] of this.materials) {
      const color = this.host.resolveColorToken(elementColorToken(category), FALLBACK);
      mats.surface.color.copy(color);
      mats.cap?.color.copy(color);
    }
    const accent = this.host.resolveColorToken('--color-accent', FALLBACK);
    this.selectedSolid?.color.copy(accent);
    this.selectedSpace?.color.copy(accent);
    this.wireframeMaterial?.color.copy(this.host.resolveColorToken('--color-text-muted', FALLBACK));
  }

  /** The element meshes a click may pick (the shown ones; not the caps). */
  pickObjects(): THREE.Object3D[] {
    if (!this.group?.visible) return [];
    return this.meshes.filter((m) => m.visible);
  }

  /**
   * The element under a pick, given the hits nearest first: the first one
   * the clipping planes leave on screen. Null when none is.
   */
  static elementOf(hits: readonly THREE.Intersection[], planes: readonly THREE.Plane[]): string | null {
    for (const h of hits) {
      if (planes.some((pl) => pl.distanceToPoint(h.point) < 0)) continue;
      const guid = h.object.userData.elementGuid;
      if (typeof guid === 'string') return guid;
    }
    return null;
  }

  dispose(): void {
    this.clear();
    for (const mats of this.materials.values()) {
      mats.surface.dispose();
      mats.cap?.dispose();
    }
    this.materials.clear();
    this.selectedSolid?.dispose();
    this.selectedSpace?.dispose();
    this.wireframeMaterial?.dispose();
    this.selectedSolid = null;
    this.selectedSpace = null;
    this.wireframeMaterial = null;
  }

  private clear(): void {
    const group = this.group;
    this.group = null;
    if (group) {
      group.removeFromParent();
      for (const m of this.meshes) m.geometry.dispose();
    }
    this.meshes = [];
    this.caps = [];
  }

  private ensureMaterials(): void {
    if (this.materials.size > 0) return;
    for (const category of ELEMENT_CATEGORIES) {
      const color = this.host.resolveColorToken(elementColorToken(category), FALLBACK);
      const translucent = isTranslucentCategory(category);
      const surface = new THREE.MeshStandardMaterial({
        color,
        side: THREE.DoubleSide,
        metalness: 0,
        roughness: 0.85,
        flatShading: true,
        transparent: translucent,
        opacity: translucent ? SPACE_OPACITY : 1,
        depthWrite: !translucent,
        // Coplanar faces (a door box in its wall, a space on its slab): the solids win.
        polygonOffset: true,
        polygonOffsetFactor: translucent ? 1 : 0,
        polygonOffsetUnits: translucent ? 4 : 0,
      });
      const cap = translucent
        ? null
        : new THREE.MeshBasicMaterial({ color, side: THREE.BackSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 });
      this.materials.set(category, { surface, cap });
    }
    const accent = this.host.resolveColorToken('--color-accent', FALLBACK);
    this.selectedSolid = new THREE.MeshStandardMaterial({ color: accent, side: THREE.DoubleSide, metalness: 0, roughness: 0.7, flatShading: true });
    this.selectedSpace = new THREE.MeshStandardMaterial({
      color: accent,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: SPACE_SELECTED_OPACITY,
      depthWrite: false,
      flatShading: true,
    });
    this.wireframeMaterial = new THREE.MeshBasicMaterial({
      color: this.host.resolveColorToken('--color-text-muted', FALLBACK),
      wireframe: true,
    });
  }

  private apply(): void {
    for (const mesh of this.meshes) {
      const category = mesh.userData.elementCategory as ElementCategory;
      const guid = mesh.userData.elementGuid as string;
      mesh.visible = !this.hidden.has(category);
      const mats = this.materials.get(category);
      if (!mats) continue;
      if (this.wireframe && this.wireframeMaterial) mesh.material = this.wireframeMaterial;
      else if (guid === this.selected) mesh.material = (isTranslucentCategory(category) ? this.selectedSpace : this.selectedSolid) ?? mats.surface;
      else mesh.material = mats.surface;
    }
    for (const cap of this.caps) cap.visible = !this.wireframe && !this.hidden.has(cap.userData.elementCategory as string);
    if (this.group) this.group.visible = this.visible;
  }
}
